import { useState, useEffect } from 'react';
import { Search, ShoppingCart, Menu, X, Phone, Mail } from 'lucide-react';

const SOCIAL_ABBR  = { facebook:'FB', instagram:'IG', twitter:'X', youtube:'YT', tiktok:'TT', whatsapp:'WA', linkedin:'LI' };
const SOCIAL_COLOR = { facebook:'#1877F2', instagram:'#E1306C', twitter:'#000', youtube:'#FF0000', tiktok:'#010101', whatsapp:'#25D366', linkedin:'#0A66C2' };

export default function HeaderClassic({ settings = {}, cartCount = 0 }) {
  const { zones = {}, style = {}, customHtml = '' } = settings;

  const allBlocks = [...(zones.left ?? []), ...(zones.center ?? []), ...(zones.right ?? [])];
  const logoBlock   = allBlocks.find(b => b.type === 'logo');
  const navBlock    = allBlocks.find(b => b.type === 'nav');
  const socialBlock = allBlocks.find(b => b.type === 'social');
  const hasSearch   = allBlocks.some(b => b.type === 'search');
  const hasCart     = allBlocks.some(b => b.type === 'cart');

  const bgColor = style.bgColor ?? '#1f2937';
  const sticky  = style.sticky  ?? true;
  const siteUrl = window.themeData?.siteUrl ?? '/';

  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [query,         setQuery]         = useState('');
  const [liveCartCount, setLiveCartCount] = useState(cartCount);

  useEffect(() => {
    const onCart = (e) => setLiveCartCount(e.detail?.count ?? 0);
    window.addEventListener('anvi:cart-update', onCart);
    return () => window.removeEventListener('anvi:cart-update', onCart);
  }, []);

  const logoEl = logoBlock
    ? (logoBlock.props?.logoType === 'image' && logoBlock.props?.logoUrl
        ? <img src={logoBlock.props.logoUrl} alt={logoBlock.props.logoText || 'Logo'} className="h-12 w-auto object-contain" />
        : <span className="text-3xl font-black bg-gradient-to-r from-primary to-accent1 bg-clip-text text-transparent">{logoBlock.props?.logoText || 'KiddieJoy'}</span>
      )
    : <span className="text-3xl font-black text-primary">KiddieJoy</span>;

  return (
    <>
      {/* Top bar */}
      <div className="text-white text-xs py-2" style={{ backgroundColor: bgColor }}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> +1 (555) 123-4567</span>
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> hello@kiddiejo y.com</span>
          </div>
          <div className="flex items-center gap-3">
            {(socialBlock?.props?.links ?? []).map(sl => (
              <a key={sl.id} href={sl.url || '#'} target="_blank" rel="noreferrer" className="w-6 h-6 rounded text-white text-xs font-bold flex items-center justify-center opacity-75 hover:opacity-100" style={{ backgroundColor: SOCIAL_COLOR[sl.platform] ?? '#555' }}>
                {SOCIAL_ABBR[sl.platform] ?? '?'}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className={`bg-white border-b-2 border-primary/20 shadow-sm${sticky ? ' sticky top-0 z-50' : ''}`}>
        {/* Logo + Search row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 gap-6">
            <a href={siteUrl} className="flex-shrink-0">{logoEl}</a>

            {hasSearch && (
              <div className="hidden md:flex flex-1 max-w-xl items-center bg-gray-50 rounded-2xl px-5 py-3 border-2 border-gray-100 focus-within:border-primary transition-colors">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search toys, cycles, books…"
                  className="bg-transparent border-none outline-none ml-3 text-sm w-full"
                />
                <button className="ml-2 px-3 py-1 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 transition-colors">Go</button>
              </div>
            )}

            <div className="flex items-center gap-3">
              {hasCart && (
                <button className="relative flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-2xl font-bold text-sm hover:bg-primary/90 transition-colors">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="hidden sm:inline">Cart</span>
                  {liveCartCount > 0 && <span className="bg-accent1 text-white text-xs font-black px-1.5 py-0.5 rounded-full">{liveCartCount}</span>}
                </button>
              )}
              <button onClick={() => setMobileOpen(true)} className="lg:hidden text-gray-600"><Menu className="w-7 h-7" /></button>
            </div>
          </div>
        </div>

        {/* Nav bar */}
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="hidden lg:flex items-center h-12 gap-1">
              {(() => {
                const _nmi = window.themeData?.navMenuItems ?? {};
                const mid = navBlock?.props?.menuId;
                const links = (mid && _nmi[mid]) ? _nmi[mid].filter(i => !i.parent) : (navBlock?.props?.links ?? []);
                return links.map(l => (
                  <a key={l.id} href={l.url} className="px-4 py-1.5 rounded-xl font-bold text-sm text-textLight hover:text-primary hover:bg-primary/5 transition-colors">
                    {l.label}
                  </a>
                ));
              })()}
            </nav>
          </div>
        </div>

        {customHtml && <div dangerouslySetInnerHTML={{ __html: customHtml }} />}
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-white overflow-y-auto">
          <div className="p-6 relative">
            <button onClick={() => setMobileOpen(false)} className="absolute top-6 right-6 text-gray-600"><X className="w-8 h-8" /></button>
            <div className="mb-8">{logoEl}</div>
            <div className="flex items-center bg-gray-100 rounded-2xl px-4 py-3 mb-8">
              <Search className="w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search…" className="bg-transparent border-none outline-none ml-3 text-sm w-full" />
            </div>
            <nav className="space-y-4">
              {(() => {
                const _nmi = window.themeData?.navMenuItems ?? {};
                const mid = navBlock?.props?.menuId;
                const links = (mid && _nmi[mid]) ? _nmi[mid].filter(i => !i.parent) : (navBlock?.props?.links ?? []);
                return links.map(l => (
                  <a key={l.id} href={l.url} className="block text-xl font-black text-textDark hover:text-primary">{l.label}</a>
                ));
              })()}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
